import { create } from '@bufbuild/protobuf'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { getPersonalSettings } from '@ebalance/proto/bffuser/v1/setting_service-SettingService_connectquery'
import {
  GetPersonalSettingsRequestSchema,
  type GetPersonalSettingsResponse,
} from '@ebalance/proto/bffuser/v1/setting_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { useIsPersonalSettingsSaving } from '~/pages/Settings/hooks/personalSettingsSaveState'

/**
 * GetPersonalSettings クエリフック。
 *
 * @param options.throwOnError 既定の `ApiClientProvider` は `throwOnError: true`
 *   (= 失敗すると error boundary = 全画面エラーに飛ばす)。 取得できなくても既定値で
 *   描画を続け、 失敗を画面内のエラー導線・fallback で扱う画面 (例: 需給ギャップ /
 *   AC-SDG-011、 スポット入札のエリア選択肢 AC-BSP-019) は `false` を渡す。
 *   個人設定を**表示フィルタの材料としてだけ**引く場合、 設定 1 本の取得失敗で
 *   画面全体を落とさず、 絞り込まない fallback で描画を続けるため。 業務エリアが
 *   未確定のまま既定値へフォールバックする点を利用者へ伝える責務は呼び出し側にある。
 */
export function useGetPersonalSettingsQuery(options?: {
  throwOnError?: boolean
  enabled?: boolean
  refetchOnWindowFocus?: boolean
}): UseQueryResult<GetPersonalSettingsResponse, ConnectError> {
  const isSaving = useIsPersonalSettingsSaving()
  return useQuery(
    getPersonalSettings,
    create(GetPersonalSettingsRequestSchema, {}),
    // throwOnError は未指定なら key 自体を渡さず QueryClient 既定 (= true) に従う
    // (`throwOnError: undefined` を渡すと既定を上書きして error boundary が無効になる)。
    {
      // 同じ query key の observer 全体で保存状態を共有し、保存中だけ focus refetch を止める。
      // 非保存時は従来どおり既定 true なので、関連画面も別タブの更新へ追随する。
      refetchOnWindowFocus:
        (options?.refetchOnWindowFocus ?? true) && !isSaving,
      ...(options?.throwOnError !== undefined
        ? { throwOnError: options.throwOnError }
        : {}),
      enabled: (options?.enabled ?? true) && !isSaving,
    },
  )
}
